import * as React from 'react'
import ReCAPTCHA from "react-google-recaptcha"
import { useTranslation } from 'react-i18next'

import Auth from 'components/Auth'

import Field from 'components/Form/Field'
import Label from 'components/Form/Label'
import Input from 'components/Form/Input'
import Button from 'components/Button'
import Alert from 'components/Alert'
import { Row } from 'components/Layout'

// routing stuff
import { Link, Redirect } from 'react-router-dom'

// reducer
import reducer, { Types, initialState, validationHelper, } from './reducer'
import { useDispatch, useSelector } from 'react-redux'
import { clearLoginErrors, signIn } from 'store/reducers/auth.thunks'
import { Creators as authCreators } from 'store/reducers/auth.reducer'

import { captchaActive, captchakey } from '../../config'
import SignInSSO from './SignInSSO'
import { SelectUserType } from 'components/Form/Select/SelectUserType'
import { useRef } from 'react'

function SignIn(props) {
  const { t, i18n } = useTranslation()
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const reduxDispatch = useDispatch()
  const { auth, geral = {} } = useSelector(state => state)
  const { fetching } = auth
  const { portalUserTypes } = geral.portalConfig
  const [emailErrorMsg, setEmailErrorMsg] = React.useState('')
  const [passwordErrorMsg, setPasswordErrorMsg] = React.useState('')

  const { useAuthSMS } = geral.portalConfig
  const [isSmsLogin, setIsSmsLogin] = React.useState(false)
  const [phone, setPhone] = React.useState('')
  const [phoneErrorMsg, setPhoneErrorMsg] = React.useState('')

  const [captchaValue, setCaptchaValue] = React.useState('')
  const recaptchaRef = useRef(null)

  const [systemLang, setSystemLang] = React.useState({ lang: i18n.language === 'pt' ? 'pt-BR' : i18n.language })
  const [portalUser, setPortalUser] = React.useState({ label: t('signup.label.PERSON'), value: 'PERSON' })
  const [showError, setShowError] = React.useState(false)

  const authEmailOrDocument = geral.portalConfig.portalAuthType === 'AUTH_REGISTRATION_EMAIL_DOCUMENT'
  const authDocument = geral.portalConfig.portalAuthType === 'AUTH_REGISTRATION_DOCUMENT'

  React.useEffect(() => {
    return () => {
      if (auth.signUpSuccess) {
        reduxDispatch(authCreators.setSignupSuccess(false))
      }
    }
  }, [auth.signUpSuccess, reduxDispatch])

  React.useEffect(() => {
    recaptchaReset()
    if (auth.error) {
      reduxDispatch(clearLoginErrors())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function recaptchaReset() {
    setCaptchaValue('')
    recaptchaRef?.current?.reset()
  }

  function callbackError(error) {
    recaptchaReset()
    setShowError(true)
  }

  const toggleSMSLogin = () => {
    setShowError(false)
    setEmailErrorMsg('')
    setPasswordErrorMsg('')
    setPhoneErrorMsg('')
    setIsSmsLogin(!isSmsLogin)
    recaptchaReset()
  }

  function submitForm() {
    setShowError(false)
    const { errors, ...data } = state
    let hasErrors = false

    if (isSmsLogin) {
      const onlyNumbersPhone = phone ? phone.replace(/[^\d]/g, "") : ""
      if (!onlyNumbersPhone || onlyNumbersPhone.length !== 11) {
        setPhoneErrorMsg(t('signin.validation.error.phone'))
        hasErrors = true
      }

      if (!hasErrors) {
        const smsData = {
          portalUser: portalUser?.value,
          recaptchaToken: captchaValue,
          phone: onlyNumbersPhone,
          email: '',
          password: '',
          loginType: 'SMS'
        }
        reduxDispatch(signIn(geral.language, smsData, callbackError))
      }
      return
    }

    if (!validationHelper.email.mandatoryValidation(state.email)) {
      dispatch({
        type: Types.ERROR,
        payload: { key: 'email' },
      })
      setEmailErrorMsg(t('signin.validation.error.email'))
      hasErrors = true
    } else if ((!authDocument && !authEmailOrDocument) && !validationHelper.email.validation(state.email)) {
      dispatch({
        type: Types.ERROR,
        payload: { key: 'email' },
      })
      setEmailErrorMsg(t('signin.validation.error.emailInvalid'))
      hasErrors = true
    }

    if (geral.portalConfig.authType) {
      if (!validationHelper.password.mandatoryValidation(state.password)) {
        dispatch({
          type: Types.ERROR,
          payload: { key: 'password' },
        })
        hasErrors = true
        setPasswordErrorMsg(t('signin.validation.error.password'))
      }
    }

    if (!hasErrors) {
      const newData = { ...data, portalUser: portalUser?.value, recaptchaToken: captchaValue }
      reduxDispatch(signIn(geral.language, newData, callbackError))
    }
  }

  function dispatcherHelper(key, value) {
    dispatch({
      type: Types.DATA,
      payload: { [key]: value, key },
    })
  }

  function updateUser({ target }) {
    setShowError(false)
    dispatcherHelper('email', target.value)
    setEmailErrorMsg("")
  }

  function updatePassword({ target }) {
    setShowError(false)
    dispatcherHelper('password', target.value)
    setPasswordErrorMsg("")
  }

  function updatePhone({ target }) {
    setShowError(false)
    setPhone(target.value)
    setPhoneErrorMsg("")
  }

  const onChangeCaptcha = _captcha => {
    setShowError(false)
    setCaptchaValue(_captcha)
  }

  if (props?.location?.forceSignOut) {
    document.location.reload(false)
    return null
  }
  if (auth.isAuthenticated) {
    return <Redirect to="/" />
  }

  if (auth.signinToken) {
    if (isSmsLogin) {
      return <Redirect to="/auth/verification?loginType=SMS" />
    } else {
      return <Redirect to="/auth/verification" />
    }
  }

  const handlePortalUser = (vl) => {
    setShowError(false)
    setPortalUser(vl)
  }

  return (
    <section>
      <Auth>
        {auth.signUpSuccess && (
          <>
            <Alert
              align="left"
              size="full"
              type="confirm"
              text={<>{t('signin.alert.signUpSuccess')}</>}
            />
            <br />
          </>
        )}
        {(auth.error || showError) && (
          <div className="alert alert-danger" role="alert">
            {geral.portalConfig.authType ?
              <div  >
                {t('signin.alert.signUpError')}{' '}
                <b>{t('signin.alert.signUpError.again')}</b>
              </div>
              :
              <div  >
                {t('yourPersonalData.message.error')}
              </div>}
          </div>
        )}

        {props?.location?.successMessage && (
          <>
            <Alert
              align="left"
              size="full"
              type="success"
              text={
                <div>
                  {props?.location?.successMessage}
                </div>
              }
            />
            <br />
          </>
        )}

        <Field>
          <header className="pvt-content-header custom">
            <h2 className="pvt-content-title custom">{geral.portalConfig.authType ? t('signin.title') : t('signin.token.title')}</h2>

            {useAuthSMS && portalUser?.value === 'PERSON' &&  (
              <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                <span
                  className="link-btn" // Certifique-se de ter uma classe CSS ou use style inline
                  style={{ cursor: 'pointer', color: 'var(--primary-color, blue)', textDecoration: 'underline' }}
                  onClick={toggleSMSLogin}
                >
                  {isSmsLogin
                    ? t('signin.back.to.email')
                    : t('signin.go.to.sms')
                  }
                </span>
              </div>
            )}

          </header>
          {portalUserTypes?.length > 1 &&
            <Field>
              <Label label={t('signin.label.access.as')} mandatory={true} />
              <SelectUserType onChange={handlePortalUser} value={portalUser} />
            </Field>
          }
          {isSmsLogin ? (
            <Field>
              <Label label={t('signup.label.phoneNumber')} relation="phone-field" />
              <Input
                id="phone-field"
                type="tel"
                value={phone}
                onChange={updatePhone}
                mask="(99)99999-9999"
                placeholder="(00)00000-0000"
                invalidMessage={phoneErrorMsg}
              />
            </Field>
          ) : (
            <>
              <Label label={authEmailOrDocument ? t('signin.label.email.or.document') : authDocument ? t('signin.label.document') : t('signin.label.email')} relation="user-field" />
              <Input
                id="user-field"
                type="text"
                value={state.email}
                onChange={updateUser}
                invalidMessage={
                  state.errors.email.error ? emailErrorMsg : null
                }
              />
            </>
          )}
        </Field>

        {
          (!isSmsLogin && geral.portalConfig.authType) ?
            <Field>
              <Label label={t('signin.label.password')} relation="password-field" />
              <Input
                id="password-field"
                type="password"
                value={state.password}
                onChange={updatePassword}
                invalidMessage={
                  state.errors.password.error ? passwordErrorMsg : null
                }
              />
            </Field>
            : ''
        }
        {captchaActive &&
          <Row position={['y-center', 'x-center']}>
            <Field className="singup-recaptcha">
              <ReCAPTCHA ref={recaptchaRef} sitekey={captchakey} onChange={onChangeCaptcha} hl={systemLang.lang} key={systemLang.lang} />
            </Field>
          </Row>
        }
        <Field>
          <Button
            disabled={(!geral.portalConfig.authType && (captchaActive ? !captchaValue : false)) || (!portalUser?.value && portalUserTypes?.length > 1)}
            type="confirm"
            label={
              geral.portalConfig.authType ?
                t('signin.button.label.send')
                : t('signin.button.label.get')}
            classie="full"
            onClick={submitForm}
            loading={fetching}
          />
          {
            !isSmsLogin && (geral.portalConfig?.ssoProviders || []).map((ssoProvider, index) =>
              <SignInSSO
                key={`SignInSSO-${++index}`}
                isDisabled={!geral.portalConfig.authType && (captchaActive ? !captchaValue : false)}
                ssoProvider={ssoProvider}
                loading={fetching}
              />
            )
          }
        </Field>
        {
          (!isSmsLogin && geral.portalConfig.authType) ?
            <Link to="/recovery-password" className="pvt-btn2 txt-al-center custom">
              {t('signin.forgotPassword')}
            </Link> : ''
        }

      </Auth>
    </section >
  )
}

export default SignIn
